import { SUPABASE_URL, SUPABASE_ANON_KEY, STATIONS_CACHE_KEY, STATIONS_CACHE_TTL } from '@shared/constants.js';
import { createClient } from '@supabase/supabase-js';

/**
 * Supabase Client — Public (anon) access
 * Reads active stations, submits suggestions.
 * Does NOT have admin privileges.
 */

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Fetches all active, healthy stations from Supabase.
 * Uses localStorage cache with TTL to reduce API calls.
 * Falls back to cache if Supabase is unavailable.
 */
export async function getActiveStations() {
  // Check cache first
  const cached = getCachedStations();
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('stations')
      .select('id, name, slug, latitude, longitude, country_code, country_name, city, state, stream_url, stream_codec, stream_bitrate, website_url, logo_url, language, tags, source')
      .eq('status', 'active')
      .eq('health_check_ok', true)
      .order('name');

    if (error) throw error;

    // Cache the results
    setCachedStations(data);
    return data;
  } catch (err) {
    console.warn('[SupabaseClient] Failed to fetch stations, using cache:', err.message);
    // Fallback to expired cache if available
    return getCachedStations(true) || [];
  }
}

/**
 * Submits a radio station suggestion from the public.
 */
export async function submitSuggestion({ stationName, streamUrl, websiteUrl, countryCode, city, submittedBy, notes }) {
  const { data, error } = await supabase
    .from('suggestions')
    .insert({
      station_name: stationName,
      stream_url: streamUrl,
      website_url: websiteUrl || null,
      country_code: countryCode || null,
      city: city || null,
      submitted_by: submittedBy || null,
      notes: notes || null,
      status: 'pending',
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ─── Cache Helpers ──────────────────────────────────────────────────────────

function getCachedStations(ignoreExpiry = false) {
  try {
    const raw = localStorage.getItem(STATIONS_CACHE_KEY);
    if (!raw) return null;

    const { data, timestamp } = JSON.parse(raw);
    const isExpired = Date.now() - timestamp > STATIONS_CACHE_TTL;

    if (!ignoreExpiry && isExpired) return null;
    return data;
  } catch {
    return null;
  }
}

function setCachedStations(data) {
  try {
    localStorage.setItem(STATIONS_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export { supabase };
